import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography
} from '@mui/material';
import { Add, Delete, Print, Replay, Save, Search } from '@mui/icons-material';
import { api } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import { formatApiError, useToast } from '../contexts/ToastContext.jsx';
import { formatDate } from '../utils/date.js';
import { printOrderOnSave } from '../utils/orderPrint.js';

function todayIso() {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0')
  ].join('-');
}

function toIsoDate(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0')
  ].join('-');
}

const emptyHeader = () => ({
  orderNumber: '',
  billBookNo: '',
  orderDate: todayIso(),
  deliveryDate: '',
  status: 'Pending'
});

const emptyItemForm = () => ({
  employeeId: '',
  productId: '',
  colorId: '',
  sizeId: '',
  quantity: 1,
  masterRate: 0,
  stitcherRate: 0
});

function money(value) {
  return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function OrdersPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [header, setHeader] = useState(emptyHeader);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [itemOpen, setItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [retrieveOpen, setRetrieveOpen] = useState(false);

  const nextNumber = useQuery({
    queryKey: ['orders', 'next-number'],
    queryFn: async () => (await api.get('/orders/next-number')).data.orderNumber
  });

  const savedOrders = useQuery({
    queryKey: ['orders', 'list'],
    queryFn: async () => (await api.get('/orders')).data,
    enabled: retrieveOpen
  });

  const employees = useQuery({
    queryKey: ['resource', 'employees'],
    queryFn: async () => (await api.get('/resources/employees')).data
  });
  const products = useQuery({
    queryKey: ['resource', 'products'],
    queryFn: async () => (await api.get('/resources/products')).data
  });
  const colors = useQuery({
    queryKey: ['resource', 'colors'],
    queryFn: async () => (await api.get('/resources/colors')).data
  });
  const sizes = useQuery({
    queryKey: ['resource', 'sizes'],
    queryFn: async () => (await api.get('/resources/sizes')).data
  });

  useEffect(() => {
    if (!editingId && nextNumber.data) {
      setHeader((current) => ({ ...current, orderNumber: nextNumber.data }));
    }
  }, [nextNumber.data, editingId]);

  const grandTotal = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.quantity) * Number(item.masterRate), 0),
    [items]
  );

  const resetNew = async () => {
    setEditingId(null);
    setItems([]);
    setItemForm(emptyItemForm());
    setHeader(emptyHeader());
    const refreshed = await queryClient.fetchQuery({
      queryKey: ['orders', 'next-number'],
      queryFn: async () => (await api.get('/orders/next-number')).data.orderNumber
    });
    setHeader({ ...emptyHeader(), orderNumber: refreshed });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!header.orderDate) {
        toast.warning('Order date is required');
        throw new Error('Order date is required');
      }
      if (!items.length) {
        toast.warning('Add at least one item');
        throw new Error('Add at least one item');
      }
      const payload = {
        billBookNo: header.billBookNo,
        orderDate: header.orderDate,
        deliveryDate: header.deliveryDate || undefined,
        status: header.status || 'Pending',
        items: items.map((item) => ({
          productId: item.productId,
          employeeId: item.employeeId,
          colorId: item.colorId || undefined,
          sizeId: item.sizeId || undefined,
          quantity: Number(item.quantity),
          masterRate: Number(item.masterRate),
          stitcherRate: Number(item.stitcherRate)
        }))
      };
      const url = editingId ? `/orders/${editingId}` : '/orders';
      return (await api[editingId ? 'put' : 'post'](url, payload, { skipErrorToast: true })).data;
    },
    onSuccess: async (order) => {
      setEditingId(order.id);
      setHeader({
        orderNumber: order.orderNumber,
        billBookNo: order.billBookNo || '',
        orderDate: toIsoDate(order.orderDate),
        deliveryDate: toIsoDate(order.deliveryDate),
        status: order.status
      });
      setItems(order.items);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success(`Order ${editingId ? 'updated' : 'saved'} successfully`);
      try {
        await printOrderOnSave(order);
      } catch (error) {
        toast.warning(error?.message || 'Order saved, but printing failed');
      }
    },
    onError: (error) => {
      if (error?.response) toast.error(formatApiError(error, 'Could not save order'));
    }
  });

  const loadOrder = useMutation({
    mutationFn: async (id) => (await api.get(`/orders/${id}`, { skipErrorToast: true })).data,
    onSuccess: (order) => {
      setEditingId(order.id);
      setHeader({
        orderNumber: order.orderNumber,
        billBookNo: order.billBookNo || '',
        orderDate: toIsoDate(order.orderDate),
        deliveryDate: toIsoDate(order.deliveryDate),
        status: order.status
      });
      setItems(order.items);
      setRetrieveOpen(false);
      toast.success(`Order ${order.orderNumber} loaded`);
    },
    onError: (error) => toast.error(formatApiError(error, 'Could not load order'))
  });

  const updateHeader = (event) => {
    const { name, value } = event.target;
    setHeader((current) => ({ ...current, [name]: value }));
  };

  const updateItemForm = (event) => {
    const { name, value } = event.target;
    setItemForm((current) => {
      const next = { ...current, [name]: value };
      if (name === 'productId') {
        const product = products.data?.find((item) => String(item.id) === String(value));
        if (product) {
          next.masterRate = Number(product.masterRate || 0);
          next.stitcherRate = Number(product.stitchingRate || 0);
        }
      }
      return next;
    });
  };

  const addItem = () => {
    if (!itemForm.employeeId) return toast.warning('Employee is required');
    if (!itemForm.productId) return toast.warning('Product is required');
    if (!(Number(itemForm.quantity) > 0)) return toast.warning('Quantity must be greater than zero');

    const employee = employees.data?.find((item) => String(item.id) === String(itemForm.employeeId));
    const product = products.data?.find((item) => String(item.id) === String(itemForm.productId));
    const color = colors.data?.find((item) => String(item.id) === String(itemForm.colorId));
    const size = sizes.data?.find((item) => String(item.id) === String(itemForm.sizeId));

    setItems((current) => [
      ...current,
      {
        key: `${Date.now()}-${current.length}`,
        productId: Number(itemForm.productId),
        itemCode: product?.sku || '',
        itemName: product?.name || '',
        employeeId: Number(itemForm.employeeId),
        employeeName: employee?.name || '',
        colorId: itemForm.colorId ? Number(itemForm.colorId) : null,
        colorName: color?.name || '',
        sizeId: itemForm.sizeId ? Number(itemForm.sizeId) : null,
        sizeName: size?.name || '',
        quantity: Number(itemForm.quantity),
        masterRate: Number(itemForm.masterRate || 0),
        stitcherRate: Number(itemForm.stitcherRate || 0)
      }
    ]);
    setItemForm(emptyItemForm());
    setItemOpen(false);
  };

  const removeItem = (index) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const headerActions = (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Button startIcon={<Add />} variant="contained" onClick={() => { setItemForm(emptyItemForm()); setItemOpen(true); }}>
        Add Order
      </Button>
      <Button startIcon={<Replay />} variant="outlined" onClick={resetNew}>New</Button>
      <Button
        startIcon={<Save />}
        variant="contained"
        color="success"
        onClick={() => save.mutate()}
        disabled={save.isPending}
      >
        Save
      </Button>
      <Button startIcon={<Search />} variant="outlined" onClick={() => setRetrieveOpen(true)}>Retrieve</Button>
      <Button
        startIcon={<Print />}
        variant="outlined"
        onClick={async () => {
          if (!header.orderNumber || !items.length) {
            toast.warning('Save the order with at least one item before printing');
            return;
          }
          try {
            await printOrderOnSave({
              orderNumber: header.orderNumber,
              billBookNo: header.billBookNo,
              orderDate: header.orderDate,
              deliveryDate: header.deliveryDate,
              items
            });
          } catch (error) {
            toast.warning(error?.message || 'Printing failed');
          }
        }}
      >
        Print
      </Button>
    </Stack>
  );

  return (
    <Box>
      <PageHeader title="Orders" subtitle="Create production orders with employee, product, and rate details" action={headerActions} />

      <Card sx={{ p: 2, mb: 2 }} className="print-area">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              size="small"
              fullWidth
              label="Order Number"
              value={header.orderNumber}
              slotProps={{ input: { readOnly: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              size="small"
              fullWidth
              name="billBookNo"
              label="Bill Book No."
              value={header.billBookNo}
              onChange={updateHeader}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              size="small"
              fullWidth
              required
              type="date"
              name="orderDate"
              label="Order Date"
              value={header.orderDate}
              onChange={updateHeader}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              size="small"
              fullWidth
              type="date"
              name="deliveryDate"
              label="Delivery Date"
              value={header.deliveryDate}
              onChange={updateHeader}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>
      </Card>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'primary.main', '& .MuiTableCell-head': { color: 'primary.contrastText', fontWeight: 700 } }}>
              <TableRow>
                <TableCell>Item Code</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">QTY</TableCell>
                <TableCell align="right">Master Rate</TableCell>
                <TableCell align="right">Stitcher Rate</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row, index) => (
                <TableRow key={row.key || row.id || index} hover>
                  <TableCell>{row.itemCode || '—'}</TableCell>
                  <TableCell sx={{ fontWeight: 650 }}>{row.itemName}</TableCell>
                  <TableCell>{row.employeeName}</TableCell>
                  <TableCell>{row.colorName || '—'}</TableCell>
                  <TableCell>{row.sizeName || '—'}</TableCell>
                  <TableCell align="right">{row.quantity}</TableCell>
                  <TableCell align="right">{money(row.masterRate)}</TableCell>
                  <TableCell align="right">{money(row.stitcherRate)}</TableCell>
                  <TableCell align="right">{money(Number(row.quantity) * Number(row.masterRate))}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => removeItem(index)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography color="text.secondary" textAlign="center" py={5}>
                      No items yet. Click Add Order to add a line.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!!items.length && (
          <Box px={2} py={1.5} display="flex" justifyContent="flex-end">
            <Typography fontWeight={700}>Grand Total: Rs {money(grandTotal)}</Typography>
          </Box>
        )}
      </Card>

      <Dialog open={itemOpen} onClose={() => setItemOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Order Item</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField size="small" required select fullWidth name="employeeId" label="Employee" value={itemForm.employeeId} onChange={updateItemForm}>
                {employees.data?.filter((item) => item.isActive).map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField size="small" required select fullWidth name="productId" label="Product" value={itemForm.productId} onChange={updateItemForm}>
                {products.data?.filter((item) => item.isActive).map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.sku ? `${item.sku} — ${item.name}` : item.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField size="small" select fullWidth name="colorId" label="Color" value={itemForm.colorId} onChange={updateItemForm}>
                <MenuItem value="">None</MenuItem>
                {colors.data?.filter((item) => item.isActive).map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField size="small" select fullWidth name="sizeId" label="Size" value={itemForm.sizeId} onChange={updateItemForm}>
                <MenuItem value="">None</MenuItem>
                {sizes.data?.filter((item) => item.isActive).map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                size="small"
                required
                fullWidth
                type="number"
                name="quantity"
                label="QTY"
                value={itemForm.quantity}
                onChange={updateItemForm}
                onFocus={(event) => event.target.select()}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                size="small"
                required
                fullWidth
                type="number"
                name="masterRate"
                label="Master Rate"
                value={itemForm.masterRate}
                onChange={updateItemForm}
                onFocus={(event) => event.target.select()}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                size="small"
                required
                fullWidth
                type="number"
                name="stitcherRate"
                label="Stitcher Rate"
                value={itemForm.stitcherRate}
                onChange={updateItemForm}
                onFocus={(event) => event.target.select()}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addItem}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={retrieveOpen} onClose={() => setRetrieveOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Retrieve Order</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'primary.main', '& .MuiTableCell-head': { color: 'primary.contrastText', fontWeight: 700 } }}>
                <TableRow>
                  <TableCell>Order Number</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Delivery Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {savedOrders.data?.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => loadOrder.mutate(row.id)}
                  >
                    <TableCell sx={{ fontWeight: 650 }}>{row.orderNumber}</TableCell>
                    <TableCell>{formatDate(row.orderDate)}</TableCell>
                    <TableCell>{row.deliveryDate ? formatDate(row.deliveryDate) : '—'}</TableCell>
                    <TableCell>{row.status}</TableCell>
                  </TableRow>
                ))}
                {!savedOrders.isLoading && !savedOrders.data?.length && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary" textAlign="center" py={4}>No saved orders found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetrieveOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
